import {
  DEFAULT_SAUNA_AUTO_START,
  DEFAULT_SAUNA_READY_STABLE_SECONDS,
  DEFAULT_SAUNA_RESET_THRESHOLD_C,
  DEFAULT_SAUNA_TARGET_TEMPERATURE_C,
  SAUNA_READY_STABLE_MAX_S,
  SAUNA_READY_STABLE_MIN_S,
  SAUNA_RESET_MAX_C,
  SAUNA_RESET_MIN_C,
  SAUNA_TARGET_MAX_C,
  SAUNA_TARGET_MIN_C,
  type SaunaFreshness,
  type SaunaPhase,
} from '@jums-dashboard/shared';
import { useEffect, useMemo, useState } from 'react';

import Badge from '@/components/Badge/Badge';
import Button from '@/components/Button/Button';
import Switch from '@/components/Switch/Switch';

import useCurrentHome from '../hooks/useCurrentHome';
import useSauna from '../hooks/useSauna';
import { useAcknowledgeSaunaReady, useFinishSauna, useStartSauna } from '../hooks/useSaunaMutations';
import useSensors from '../hooks/useSensors';
import useUpdateSaunaConfig from '../hooks/useUpdateSaunaConfig';
import { formatDateMs } from '../lib/format';
import AsyncSection from './AsyncSection';
import FieldMessage from './FieldMessage';

const PHASE_TONE: Record<SaunaPhase, 'success' | 'warning' | 'neutral' | 'info'> = {
  idle: 'neutral',
  warming: 'info',
  ready: 'success',
  inUse: 'success',
  cooldown: 'warning',
  stale: 'neutral',
};

const PHASE_LABEL: Record<SaunaPhase, string> = {
  idle: 'idle',
  warming: 'warming',
  ready: 'ready',
  inUse: 'in use',
  cooldown: 'cooldown',
  stale: 'stale',
};

const FRESHNESS_TONE: Record<SaunaFreshness, 'success' | 'warning' | 'neutral'> = {
  fresh: 'success',
  stale: 'warning',
  unknown: 'neutral',
};

function fmtTempC(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)} °C`;
}

function fmtIso(value: string | null): string {
  if (!value) return '—';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '—';
  return formatDateMs(ms);
}

export default function SaunaSection() {
  const homeQuery = useCurrentHome();
  const homeId = homeQuery.data?.id;
  const sauna = useSauna(homeId);
  const sensors = useSensors(homeId);

  const loading = homeQuery.isPending || sauna.isPending || sensors.isPending;
  const error = homeQuery.isError || !homeId || sauna.isError || sensors.isError;
  const errorMessage = homeQuery.isError || !homeId ? 'Failed to load home.' : 'Failed to load sauna state.';
  const retry = () => {
    if (homeQuery.isError) void homeQuery.refetch();
    if (sauna.isError) void sauna.refetch();
    if (sensors.isError) void sensors.refetch();
  };

  return (
    <AsyncSection loading={loading} error={error} errorMessage={errorMessage} onRetry={retry}>
      {homeId && <SaunaSectionBody homeId={homeId} />}
    </AsyncSection>
  );
}

function SaunaSectionBody({ homeId }: { homeId: string }) {
  const sauna = useSauna(homeId);
  const sensors = useSensors(homeId);
  const update = useUpdateSaunaConfig();
  const startMut = useStartSauna();
  const finishMut = useFinishSauna();
  const ackMut = useAcknowledgeSaunaReady();

  const cfg = sauna.data?.config;
  const cs = sauna.data?.currentState;
  const enabledSensors = useMemo(() => (sensors.data ?? []).filter((s) => s.enabled), [sensors.data]);

  const [sensorId, setSensorId] = useState<string>(cfg?.sensorId ?? '');
  const [targetText, setTargetText] = useState<string>(String(cfg?.targetTemperatureC ?? DEFAULT_SAUNA_TARGET_TEMPERATURE_C));
  const [stableText, setStableText] = useState<string>(String(cfg?.readyStableSeconds ?? DEFAULT_SAUNA_READY_STABLE_SECONDS));
  const [resetText, setResetText] = useState<string>(String(cfg?.resetThresholdC ?? DEFAULT_SAUNA_RESET_THRESHOLD_C));
  const [autoStartEnabled, setAutoStartEnabled] = useState<boolean>(cfg?.autoStart?.enabled ?? DEFAULT_SAUNA_AUTO_START.enabled);
  const [isDirty, setIsDirty] = useState(false);

  // Re-sync local state when server data changes — but only when the user
  // hasn't started editing yet. Otherwise the 30 s refetch would clobber
  // in-progress edits.
  useEffect(() => {
    if (!cfg) return;
    if (isDirty) return;
    setSensorId(cfg.sensorId ?? '');
    setTargetText(String(cfg.targetTemperatureC));
    setStableText(String(cfg.readyStableSeconds));
    setResetText(String(cfg.resetThresholdC));
    setAutoStartEnabled(cfg.autoStart?.enabled ?? DEFAULT_SAUNA_AUTO_START.enabled);
  }, [cfg?.sensorId, cfg?.targetTemperatureC, cfg?.readyStableSeconds, cfg?.resetThresholdC, cfg?.autoStart?.enabled, isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  const parsedTarget = Number(targetText);
  const parsedStable = Number(stableText);
  const parsedReset = Number(resetText);
  const targetValid = Number.isFinite(parsedTarget) && parsedTarget >= SAUNA_TARGET_MIN_C && parsedTarget <= SAUNA_TARGET_MAX_C;
  const stableValid =
    Number.isFinite(parsedStable) &&
    Number.isInteger(parsedStable) &&
    parsedStable >= SAUNA_READY_STABLE_MIN_S &&
    parsedStable <= SAUNA_READY_STABLE_MAX_S;
  const resetValid = Number.isFinite(parsedReset) && parsedReset >= SAUNA_RESET_MIN_C && parsedReset <= SAUNA_RESET_MAX_C;
  const resetBelowTarget = !targetValid || !resetValid || parsedReset < parsedTarget;

  const targetError = !targetValid ? `Target must be between ${SAUNA_TARGET_MIN_C} and ${SAUNA_TARGET_MAX_C} °C.` : '';
  const stableError = !stableValid
    ? `Ready-stable window must be a whole number between ${SAUNA_READY_STABLE_MIN_S} and ${SAUNA_READY_STABLE_MAX_S} s.`
    : '';
  const resetRangeError = !resetValid ? `Reset threshold must be between ${SAUNA_RESET_MIN_C} and ${SAUNA_RESET_MAX_C} °C.` : '';
  const resetTargetError = !resetRangeError && !resetBelowTarget ? 'Reset threshold must be below target temperature.' : '';
  const resetError = resetRangeError || resetTargetError;

  const dirty = isDirty;
  const canSave = targetValid && stableValid && resetValid && resetBelowTarget && dirty && !update.isPending;

  const phase: SaunaPhase = cs?.phase ?? 'idle';
  const canStart = !!cfg?.sensorId && (phase === 'idle' || phase === 'cooldown' || phase === 'stale');
  const canAck = phase === 'ready';
  const canFinish = phase === 'ready' || phase === 'inUse';

  const startDisabledReason = !cfg?.sensorId ? 'Pick a sensor below first.' : !canStart ? 'A sauna session is already active.' : '';
  const ackDisabledReason = !canAck ? 'Only available when the sauna is ready.' : '';
  const finishDisabledReason = !canFinish ? 'Only available once the sauna is ready or in use.' : '';

  const handleSave = () => {
    if (!canSave) return;
    update.mutate(
      {
        homeId,
        sensorId: sensorId === '' ? null : sensorId,
        targetTemperatureC: parsedTarget,
        readyStableSeconds: parsedStable,
        resetThresholdC: parsedReset,
        autoStart: { enabled: autoStartEnabled },
      },
      {
        onSuccess: () => setIsDirty(false),
      },
    );
  };

  const currentAutoStart = cfg?.autoStart?.enabled ?? DEFAULT_SAUNA_AUTO_START.enabled;

  return (
    <div className="space-y-5">
      {/* Live state */}
      <section
        aria-labelledby="sauna-state-heading"
        className="space-y-3 rounded-lg border border-(--border-subtle) bg-(--bg-surface) px-4 py-3"
      >
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="sauna-state-heading" className="text-sm font-semibold text-(--text-primary)">
            Current state
          </h3>
          <Badge tone={PHASE_TONE[phase]}>{PHASE_LABEL[phase]}</Badge>
          {cs && <Badge tone={FRESHNESS_TONE[cs.freshness]}>{cs.freshness}</Badge>}
          <Badge tone={currentAutoStart ? 'info' : 'neutral'}>auto-start {currentAutoStart ? 'on' : 'off'}</Badge>
        </div>
        {!sauna.data?.exists ? (
          <p className="text-sm text-(--text-secondary)">No sauna configuration yet. Pick a sensor below and save to initialize.</p>
        ) : (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Row label="Temperature" value={fmtTempC(cs?.currentTemperatureC ?? null)} />
            <Row label="Target" value={fmtTempC(cs?.targetTemperatureC ?? cfg?.targetTemperatureC ?? null)} />
            <Row label="Session id" value={cs?.sessionId ?? '—'} mono />
            <Row label="Estimated ready" value={fmtIso(cs?.estimatedReadyAt ?? null)} />
            <Row label="Last transition" value={fmtIso(cs?.lastTransitionAt ?? null)} />
            <Row label="Updated" value={fmtIso(cs?.updatedAt ?? null)} />
          </dl>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <SaunaActionButton
            label="Start"
            variant="primary"
            onClick={() => startMut.mutate({ homeId })}
            loading={startMut.isPending}
            disabled={!canStart}
            reason={startDisabledReason}
          />
          <SaunaActionButton
            label="Acknowledge ready"
            variant="secondary"
            onClick={() => ackMut.mutate({ homeId })}
            loading={ackMut.isPending}
            disabled={!canAck}
            reason={ackDisabledReason}
          />
          <SaunaActionButton
            label="Done"
            variant="secondary"
            onClick={() => finishMut.mutate({ homeId })}
            loading={finishMut.isPending}
            disabled={!canFinish}
            reason={finishDisabledReason}
          />
        </div>
      </section>

      {/* Config */}
      <form
        className="space-y-3 rounded-lg border border-(--border-subtle) bg-(--bg-surface-strong) px-4 py-3"
        aria-labelledby="sauna-config-heading"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <h3 id="sauna-config-heading" className="text-sm font-semibold text-(--text-primary)">
          Configuration
        </h3>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
            Sensor
            <select
              value={sensorId}
              onChange={(e) => {
                markDirty();
                setSensorId(e.target.value);
              }}
              className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
            >
              <option value="">— None —</option>
              {enabledSensors.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.location ? ` (${s.location})` : ''}
                </option>
              ))}
            </select>
            {enabledSensors.length === 0 && <FieldMessage>No enabled sensors. Promote one from the Unknown sensors tab.</FieldMessage>}
          </label>
          <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
            Target temperature (°C)
            <input
              type="number"
              step="0.5"
              min={SAUNA_TARGET_MIN_C}
              max={SAUNA_TARGET_MAX_C}
              value={targetText}
              onChange={(e) => {
                markDirty();
                setTargetText(e.target.value);
              }}
              aria-invalid={!targetValid || undefined}
              className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
            />
            {targetError ? (
              <FieldMessage tone="error">{targetError}</FieldMessage>
            ) : (
              <FieldMessage>
                {SAUNA_TARGET_MIN_C}–{SAUNA_TARGET_MAX_C}. Default {DEFAULT_SAUNA_TARGET_TEMPERATURE_C}.
              </FieldMessage>
            )}
          </label>
          <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
            Ready-stable window (s)
            <input
              type="number"
              step="1"
              min={SAUNA_READY_STABLE_MIN_S}
              max={SAUNA_READY_STABLE_MAX_S}
              value={stableText}
              onChange={(e) => {
                markDirty();
                setStableText(e.target.value);
              }}
              aria-invalid={!stableValid || undefined}
              className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
            />
            {stableError ? (
              <FieldMessage tone="error">{stableError}</FieldMessage>
            ) : (
              <FieldMessage>
                {SAUNA_READY_STABLE_MIN_S}–{SAUNA_READY_STABLE_MAX_S}. Default {DEFAULT_SAUNA_READY_STABLE_SECONDS}.
              </FieldMessage>
            )}
          </label>
          <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
            Reset threshold (°C)
            <input
              type="number"
              step="0.5"
              min={SAUNA_RESET_MIN_C}
              max={SAUNA_RESET_MAX_C}
              value={resetText}
              onChange={(e) => {
                markDirty();
                setResetText(e.target.value);
              }}
              aria-invalid={!resetValid || !resetBelowTarget || undefined}
              className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
            />
            {resetError ? (
              <FieldMessage tone="error">{resetError}</FieldMessage>
            ) : (
              <FieldMessage>
                {SAUNA_RESET_MIN_C}–{SAUNA_RESET_MAX_C}. Default {DEFAULT_SAUNA_RESET_THRESHOLD_C}. Must be below target.
              </FieldMessage>
            )}
          </label>
        </div>

        {/* Auto-start placeholder (Phase 12 will wire detection logic). */}
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-(--border-subtle) bg-(--bg-surface) px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-(--text-primary)">Auto-start (placeholder)</div>
            <FieldMessage>
              When enabled, future Phase 12 detection will start a sauna session automatically when warming is detected. No effect yet.
            </FieldMessage>
          </div>
          <Switch
            label="Enable auto-start"
            hideLabel
            checked={autoStartEnabled}
            onChange={(next) => {
              markDirty();
              setAutoStartEnabled(next);
            }}
          />
        </div>

        <div className="flex flex-col items-end gap-1 pt-1">
          <Button type="submit" variant="primary" size="sm" loading={update.isPending} disabled={!canSave}>
            Save changes
          </Button>
          {!canSave && dirty && !update.isPending && resetTargetError && <FieldMessage tone="error">{resetTargetError}</FieldMessage>}
        </div>
      </form>
    </div>
  );
}

interface ActionProps {
  label: string;
  variant: 'primary' | 'secondary';
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
  reason: string;
}

function SaunaActionButton({ label, variant, onClick, loading, disabled, reason }: ActionProps) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <Button type="button" variant={variant} size="sm" onClick={onClick} loading={loading} disabled={disabled || loading}>
        {label}
      </Button>
      {disabled && reason && <FieldMessage>{reason}</FieldMessage>}
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-(--text-tertiary)">{label}</dt>
      <dd className={mono ? 'truncate font-mono text-xs text-(--text-primary)' : 'text-(--text-primary)'}>{value}</dd>
    </div>
  );
}
